import { ContentCopy, Sort } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useCallback } from 'react';
import { useCopyToClipboard } from '../../../../../hooks/useCopyToClipboard';
import {
  CollectionSortBy,
  collectionSortByText,
} from '../../../../../util/collectionSort';
import * as styles from './HeaderSection.styles';

export interface HeaderSectionProps {
  collections?: Array<{ collectionId: number }>;
  sortBy: CollectionSortBy;
  onSortChange?: (sortBy: CollectionSortBy) => void;
}

export const HeaderSection = ({
  collections,
  sortBy,
  onSortChange,
}: HeaderSectionProps) => {
  const { copyToClipboard } = useCopyToClipboard();

  const handleCopyAllIds = useCallback(async () => {
    if (!collections || collections.length === 0) return;
    const ids = collections.map((c) => c.collectionId).join(', ');
    copyToClipboard(ids, `${collections.length} collection IDs`);
  }, [collections, copyToClipboard]);

  const handleSortChange = (event: SelectChangeEvent<string>) => {
    const newSortBy = event.target.value as CollectionSortBy;
    onSortChange?.(newSortBy);
  };

  // When default, the select has no value (empty string) and shows only the label
  // When a sort is selected, the select has a value and shows the label + value
  const selectValue = sortBy === CollectionSortBy.DEFAULT ? '' : sortBy;
  const hasValue = sortBy !== CollectionSortBy.DEFAULT;

  return (
    <Box component="header" sx={styles.header}>
      <Box sx={styles.headerContainer}>
        <Typography variant="h4" color="text.primary">
          Collections
        </Typography>

        <Box sx={styles.buttonContainer}>
          <Button
            variant="contained"
            startIcon={<ContentCopy />}
            onClick={handleCopyAllIds}
            disabled={!collections || collections.length === 0}
            sx={styles.copyButton}
          >
            Copy all collection IDs
          </Button>

          <FormControl size="small" variant="outlined" sx={styles.sortFormControl}>
            {hasValue && <InputLabel id="sort-select-label">Sort By</InputLabel>}
            <Select
              labelId="sort-select-label"
              id="sort-select"
              value={selectValue}
              label={hasValue ? 'Sort By' : undefined}
              onChange={handleSortChange}
              displayEmpty
              startAdornment={
                <InputAdornment position="start">
                  <Sort fontSize="small" />
                </InputAdornment>
              }
              inputProps={{ 'aria-label': 'Sort collections' }}
              renderValue={(value) => {
                if (!value) {
                  return <Typography sx={styles.sortPlaceholder}>Sort By</Typography>;
                }
                return collectionSortByText(value as CollectionSortBy);
              }}
            >
              <MenuItem value={CollectionSortBy.DATE_ADDED}>
                {collectionSortByText(CollectionSortBy.DATE_ADDED)}
              </MenuItem>
              <MenuItem value={CollectionSortBy.ALPHABETICAL}>
                {collectionSortByText(CollectionSortBy.ALPHABETICAL)}
              </MenuItem>
              {hasValue && (
                <MenuItem value={CollectionSortBy.DEFAULT}>Clear sort</MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
      </Box>
    </Box>
  );
};
